"use client";

import { useCallback, useEffect, useState } from "react";
import { useInjectStyles } from "../utils/useInjectStyles";

export const THEME_STORAGE_KEY = "ccheng_theme";

/** 明 / 暗 / 跟隨系統。預設 system。 */
export type ThemeMode = "light" | "dark" | "system";

const ORDER: ThemeMode[] = ["system", "light", "dark"];
const LABEL: Record<ThemeMode, string> = { system: "跟隨系統", light: "淺色", dark: "深色" };
const ICON: Record<ThemeMode, string> = { system: "🖥", light: "☀", dark: "🌙" };

/**
 * 讀取已儲存的主題。無效值或讀取失敗一律回 "system"
 *（無痕視窗、已封鎖站台資料的環境會在存取時直接丟例外）。
 */
export function readStoredTheme(): ThemeMode {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

/**
 * 把主題套到 <html> 上。
 *
 * "system" 時**移除** data-theme 屬性而不是寫入 "system" —— styles.ts 的深色規則
 * 是 `@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) }`
 * 與 `:root[data-theme="dark"]` 兩段，前者靠「沒有 data-theme」來讓系統設定生效。
 * 寫入任何非 light/dark 的值都會讓兩段規則同時落空，變成永遠淺色。
 */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", mode);
}

/**
 * 在 React 掛載**之前**就套用已儲存主題的行內腳本。
 *
 * 必須放進 <head>：否則首屏會先以淺色繪製一次、React 掛載後才切成深色，
 * 產生一次白色閃爍（深色模式使用者在夜間特別刺眼）。
 * 以字串形式匯出，供 Next.js 的 layout 用 <script dangerouslySetInnerHTML> 內嵌。
 */
export const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem(${JSON.stringify(
  THEME_STORAGE_KEY,
)});if(t==="light"||t==="dark"){document.documentElement.setAttribute("data-theme",t)}}catch(e){}})()`;

export interface ThemeToggleProps {
  /** 只顯示圖示、不顯示文字（頂欄空間有限時用）。 */
  iconOnly?: boolean;
}

/** 明/暗/跟隨系統三態切換鈕。點擊循環，選擇存 localStorage。 */
export function ThemeToggle({ iconOnly = true }: ThemeToggleProps) {
  useInjectStyles();
  // SSR 與首次繪製時一律當作 system —— 伺服器讀不到 localStorage，
  // 若在此猜一個值會與 THEME_INIT_SCRIPT 已經套上的屬性打架（hydration 不一致）。
  const [mode, setMode] = useState<ThemeMode>("system");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setMode(readStoredTheme());
    setReady(true);
  }, []);

  const cycle = useCallback(() => {
    setMode((cur) => {
      const next = ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length];
      applyTheme(next);
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* 寫入失敗不影響本次切換，只是重新整理後會回到預設 */
      }
      return next;
    });
  }, []);

  return (
    <button
      type="button"
      className="cui-reset cui-theme-toggle"
      onClick={cycle}
      // 掛載完成前不宣告目前狀態，避免螢幕閱讀器唸出與畫面不符的值
      aria-label={ready ? `主題：${LABEL[mode]}（點擊切換）` : "切換主題"}
      title={ready ? `主題：${LABEL[mode]}` : undefined}
    >
      <span aria-hidden>{ICON[mode]}</span>
      {!iconOnly && <span>{LABEL[mode]}</span>}
    </button>
  );
}
